<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useSession } from '../lib/session'
import { IconChevronLeft, IconChevronRight, IconGrid, IconLayers, IconScroll, IconUsers } from './Icons'
import { cx } from './ui'
import { Spinner } from './ui'

const NAV = [
  { to: '/', label: '概览', icon: IconGrid, end: true },
  { to: '/users', label: '账号', icon: IconUsers, end: false },
  { to: '/audit', label: '日志', icon: IconScroll, end: false },
  { to: '/catalog', label: '目录', icon: IconLayers, end: false },
]

const { user, status } = useSession()
const collapsed = ref(localStorage.getItem('admin-rail') === '1')

function toggleRail() {
  collapsed.value = !collapsed.value
  localStorage.setItem('admin-rail', collapsed.value ? '1' : '0')
}
const route = useRoute()
const router = useRouter()

watch(
  status,
  (s) => {
    if (s === 'anonymous') {
      void router.replace({ path: '/login', query: { from: route.fullPath } })
    }
  },
  { immediate: true },
)

function isActive(to: string, end: boolean) {
  if (end) return route.path === to
  return route.path === to || route.path.startsWith(`${to}/`)
}

const checking = computed(() => status.value === 'checking')

const initials = computed(() => {
  const email = user.value?.email ?? ''
  const local = email.split('@')[0] || '?'
  return local.slice(0, 2).toUpperCase()
})
</script>

<template>
  <div v-if="checking" class="flex min-h-dvh items-center justify-center gap-3 text-muted">
    <Spinner :size="18" />
    <span class="text-[13px]">校验会话…</span>
  </div>
  <div v-else-if="status === 'authenticated'" class="flex min-h-dvh">
    <aside
      :class="
        cx(
          'fixed top-0 left-0 z-20 flex h-dvh max-h-dvh flex-col overflow-hidden border-r border-[var(--color-rail-line)] bg-[var(--color-rail)] text-white transition-[width] duration-200',
          collapsed ? 'w-[72px]' : 'w-[232px]',
        )
      "
    >
      <div :class="cx('shrink-0 pt-5 pb-4', collapsed ? 'px-3' : 'px-4')">
        <img v-if="!collapsed" src="/logo.png" alt="Reellax" class="h-7 w-auto" />
        <img v-else src="/favicon.png" alt="Reellax" class="mx-auto size-7" />
        <p
          v-if="!collapsed"
          class="mt-3 text-[11px] font-medium tracking-[0.14em] text-[var(--color-rail-muted)] uppercase"
        >
          运营后台
        </p>
      </div>

      <nav class="mt-1 min-h-0 flex-1 overflow-y-auto px-2.5" aria-label="主导航">
        <div class="flex flex-col gap-0.5 pb-2">
          <RouterLink
            v-for="item in NAV"
            :key="item.to"
            :to="item.to"
            :title="collapsed ? item.label : undefined"
            :class="
              cx(
                'group relative flex items-center gap-2.5 rounded-lg py-2.5 text-[14px] transition-all duration-150',
                collapsed ? 'justify-center px-2' : 'px-2.5',
                isActive(item.to, item.end)
                  ? 'bg-[var(--color-rail-active)] font-semibold text-white shadow-[inset_0_0_0_1px_rgb(13_186_160_/_0.22)]'
                  : 'text-[var(--color-rail-muted)] hover:bg-[var(--color-rail-hover)] hover:text-white',
              )
            "
          >
            <span
              :class="
                cx(
                  'absolute top-1/2 left-0 h-5 w-[3px] -translate-y-1/2 rounded-r-full transition-colors',
                  isActive(item.to, item.end) ? 'bg-[var(--color-accent)]' : 'bg-transparent',
                )
              "
            />
            <span
              :class="
                cx(
                  'flex size-7 items-center justify-center rounded-md transition-colors',
                  isActive(item.to, item.end)
                    ? 'bg-[rgb(13_186_160_/_0.16)] text-[var(--color-accent)]'
                    : 'bg-white/[0.04] text-current group-hover:bg-white/[0.07]',
                )
              "
            >
              <component :is="item.icon" :size="15" />
            </span>
            <span v-if="!collapsed" class="truncate">{{ item.label }}</span>
          </RouterLink>
        </div>
      </nav>

      <div
        class="shrink-0 border-t border-[var(--color-rail-line)] px-3 pt-3.5 pb-[max(0.875rem,env(safe-area-inset-bottom))]"
      >
        <div
          :class="
            cx(
              'flex items-center gap-2.5 rounded-lg bg-white/[0.03] py-2',
              collapsed ? 'justify-center px-1.5' : 'px-2',
            )
          "
        >
          <template v-if="!collapsed">
            <span
              class="flex size-8 shrink-0 items-center justify-center rounded-full bg-[rgb(13_186_160_/_0.18)] text-[11px] font-semibold tracking-wide text-[var(--color-accent)]"
              aria-hidden="true"
            >
              {{ initials }}
            </span>
            <span
              class="min-w-0 flex-1 truncate text-[13px] text-[var(--color-rail-muted)]"
              :title="user?.email"
            >
              {{ user?.email }}
            </span>
          </template>
          <button
            type="button"
            :title="collapsed ? '展开侧边栏' : '收起侧边栏'"
            :aria-label="collapsed ? '展开侧边栏' : '收起侧边栏'"
            :aria-expanded="!collapsed"
            class="rounded-md p-1.5 text-[var(--color-rail-muted)] transition-colors hover:bg-[var(--color-rail-hover)] hover:text-white"
            @click="toggleRail"
          >
            <IconChevronRight v-if="collapsed" :size="18" />
            <IconChevronLeft v-else :size="18" />
          </button>
        </div>
      </div>
    </aside>

    <div :class="cx('min-w-0 flex-1 transition-[margin] duration-200', collapsed ? 'ml-[72px]' : 'ml-[232px]')">
      <RouterView v-slot="{ Component }">
        <KeepAlive :include="['Catalog']">
          <component :is="Component" />
        </KeepAlive>
      </RouterView>
    </div>
  </div>
</template>
